/**
 * Form building blocks with the prototype's markup (ui.js `field`, `textarea`, `select`, `toggle`,
 * `section`): `.field > span + input`, `.switch-row > span + input + i`, `.editor-section` with a heading
 * column and a body column. Controlled components; every editor composes these.
 */

import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  label: ReactNode;
  value: string | number;
  onChange?(value: string): void;
  /** Supporting text under the input (`.field-help`). */
  help?: ReactNode;
}

export function Field({ label, value, onChange, help, type = 'text', ...rest }: FieldProps) {
  return (
    <>
      <label className="field">
        <span>{label}</span>
        <input type={type} value={value} onChange={(e) => onChange?.(e.target.value)} {...rest} />
      </label>
      {help ? <p className="field-help">{help}</p> : null}
    </>
  );
}

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange' | 'value'> {
  label: ReactNode;
  value: string;
  onChange?(value: string): void;
}

export function TextArea({ label, value, onChange, rows = 3, ...rest }: TextAreaProps) {
  return (
    <label className="field">
      <span>{label}</span>
      <textarea rows={rows} value={value} onChange={(e) => onChange?.(e.target.value)} {...rest} />
    </label>
  );
}

export type SelectOption<V extends string> = V | readonly [V, string];

export interface SelectProps<V extends string> extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  label: ReactNode;
  value: V;
  options: ReadonlyArray<SelectOption<V>>;
  onChange?(value: V): void;
}

export function Select<V extends string>({ label, value, options, onChange, ...rest }: SelectProps<V>) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange?.(e.target.value as V)} {...rest}>
        {options.map((o) => {
          const [v, t] = typeof o === 'string' ? [o, o] : o;
          return (
            <option key={v} value={v}>
              {t}
            </option>
          );
        })}
      </select>
    </label>
  );
}

export interface ToggleProps {
  label: ReactNode;
  checked: boolean;
  onChange?(checked: boolean): void;
  help?: ReactNode;
  name?: string;
  disabled?: boolean;
  /** For a switch without a visible label (the editor header's Enabled switch). */
  ariaLabel?: string;
  className?: string;
}

/** The prototype's `.switch-row`: label text, a visually hidden checkbox and the drawn track `<i>`. */
export function Toggle({ label, checked, onChange, help, name, disabled, ariaLabel, className }: ToggleProps) {
  return (
    <label className={className ? `switch-row ${className}` : 'switch-row'}>
      <span>
        {label}
        {help ? <small>{help}</small> : null}
      </span>
      <input type="checkbox" name={name} checked={checked} disabled={disabled} aria-label={ariaLabel} onChange={(e) => onChange?.(e.target.checked)} />
      <i />
    </label>
  );
}

export interface SectionProps {
  title: ReactNode;
  help?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** `.editor-section`: heading column (title + optional help) and the body column. */
export function Section({ title, help, children, className }: SectionProps) {
  return (
    <section className={className ? `editor-section ${className}` : 'editor-section'}>
      <div className="editor-section-heading">
        <h2>{title}</h2>
        {help ? <p>{help}</p> : null}
      </div>
      <div className="editor-section-body">{children}</div>
    </section>
  );
}

/** `.form-grid`: two columns of fields. */
export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="form-grid">{children}</div>;
}
